import { inside, type Spot } from './geo';

/**
 * Sharing stops out across couriers (Routr Dispatch's Share out): pure, with no Google calls and no React, so it's tested on
 * its own (tests/share.test.ts). Google's positions only pass through it in memory.
 *
 * 1. Zone stops go to their couriers: a stop inside a zone that has couriers goes only to them; where zones overlap, to any
 *    of theirs; a stop outside every zone can go to anyone. When none of a zone's couriers is picked, its stops are left
 *    out, saying so.
 * 2. The rest are grouped by area from the start: seeds swept round the start (as runs.dart sweeps runs), then balanced
 *    clusters, so each courier gets one compact area. Stops with one obvious courier are placed first ("regret").
 * 3. Stops on the edges of areas move until the shares are even: by stops (within 1), or by time.
 * 4. Each route is ordered by nearest neighbour, then 2-opt, on straight lines from the start.
 * 5. Where two couriers' routes cross, routes are turned round, swap tails or stops, or pass a stop on, until they don't.
 *
 * A pickup and its delivery stay on one route, the pickup first. Distances are straight lines on a flat projection of the
 * city; times at 30 km/h with 3 minutes at each stop, as routing.ts `straight` times them. The same input gives the same
 * answer, whatever order the stops come in.
 */

export type Balance = 'stops' | 'time' | 'fastest';

export interface ShareStop extends Spot {
  id: string;
  /** The pickup that feeds this delivery: both go on one route, the pickup first. */
  pickupId?: string | null;
}

export interface ShareCourier {
  uid: string;
  /** The most stops on their route. */
  max: number;
}

export interface ShareZone {
  id: string;
  name: string;
  couriers: string[];
  path: Spot[];
}

export interface ShareInput {
  stops: ShareStop[];
  /** The couriers who may take stops, in the owner's order. */
  couriers: ShareCourier[];
  /** As few as needed: only as many of [couriers] as the stops need; otherwise each one gets a share. */
  fewest?: boolean;
  balance: Balance;
  zones?: ShareZone[];
  /** Where routes start (the shop); null: wherever each courier is, and the stops' middle groups them. */
  start: Spot | null;
  /** Time at each stop (s) and the driving speed (m/s): 3 minutes and 30 km/h, as routing.ts `straight`. */
  dwellS?: number;
  speed?: number;
}

/** Why a stop is left out, naming the courier or the rule. */
export type LeftWhy =
  /** In zones whose couriers ([couriers]) aren't among those picked. */
  | { kind: 'zone'; zones: string[]; couriers: string[] }
  /** Every courier it may go to is at their most stops; [uid] is the one it would have gone to. */
  | { kind: 'full'; uid: string; max: number; zones: string[] }
  /** Its pickup and its delivery have no courier in common (their zones' couriers differ). */
  | { kind: 'apart'; other: string; zones: string[] }
  /** No courier picked. */
  | { kind: 'nobody' };

export interface ShareRoute {
  uid: string;
  /** Stop ids in the order they're visited. */
  ids: string[];
  distanceM: number;
  drivingS: number;
  /** Driving and the time at each stop. */
  totalS: number;
}

export interface ShareResult {
  /** One per courier who takes stops (or, picked by the owner, would): in the order the couriers were given. */
  routes: ShareRoute[];
  left: { id: string; why: LeftWhy }[];
}

/** A stop on the flat projection, in metres from the middle. */
interface P {
  x: number;
  y: number;
}

/** Stops that go together: a pickup and the deliveries it feeds, or one stop. */
interface Unit extends P {
  /** Member stops (indexes into the stops), pickups first. */
  members: number[];
  n: number;
  /** Courier indexes it may go to; null: anyone. */
  allowed: number[] | null;
  zones: string[];
}

const DWELL_S = 180;
const SPEED = (30 * 1000) / 3600;

/** A flat projection of the city, good to a fraction of a percent over tens of km. */
function projector(points: Spot[]) {
  const lat0 = points.reduce((a, p) => a + p.lat, 0) / Math.max(1, points.length);
  const lng0 = points.reduce((a, p) => a + p.lng, 0) / Math.max(1, points.length);
  const kx = 111_320 * Math.cos((lat0 * Math.PI) / 180);
  const ky = 110_540;
  return (p: Spot): P => ({ x: (p.lng - lng0) * kx, y: (p.lat - lat0) * ky });
}

const dist = (a: P, b: P) => Math.hypot(a.x - b.x, a.y - b.y);

/** How far a stop is from a courier's area, as the grouping counts it: see [petals]. */
type Cost = (u: P, c: P) => number;

/**
 * Distance with the part along the way out from the start counted at [w] of itself, so areas grow as petals from the
 * start rather than rings round it: a courier's drive out to their area then doesn't cross another's (ring-shaped areas
 * round a shop in the middle of town put the inner courier's route across everyone else's way out).
 */
function petals(origin: P, w = 0.4): Cost {
  return (u, c) => {
    const ux = u.x - origin.x;
    const uy = u.y - origin.y;
    const cx = c.x - origin.x;
    const cy = c.y - origin.y;
    const ru = Math.hypot(ux, uy);
    const rc = Math.hypot(cx, cy);
    if (ru < 1 || rc < 1) return dist(u, c);
    let dt = Math.abs(Math.atan2(uy, ux) - Math.atan2(cy, cx));
    if (dt > Math.PI) dt = 2 * Math.PI - dt;
    return Math.hypot(w * (ru - rc), ((ru + rc) / 2) * dt);
  };
}

export function shareOut(input: ShareInput): ShareResult {
  const dwell = input.dwellS ?? DWELL_S;
  const speed = input.speed ?? SPEED;
  // Order-independent: the same stops in any order give the same answer.
  const stops = [...input.stops].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const couriers = input.couriers.filter((c, i, all) => c.max > 0 && all.findIndex((x) => x.uid === c.uid) === i);
  const left: ShareResult['left'] = [];
  if (!stops.length) return { routes: couriers.map((c) => emptyRoute(c.uid)), left };
  if (!couriers.length) return { routes: [], left: stops.map((s) => ({ id: s.id, why: { kind: 'nobody' } })) };

  const proj = projector(input.start ? [...stops, input.start] : stops);
  const pts = stops.map(proj);
  const origin: P = input.start ? proj(input.start) : { x: pts.reduce((a, p) => a + p.x, 0) / pts.length, y: pts.reduce((a, p) => a + p.y, 0) / pts.length };
  const index = new Map(stops.map((s, i) => [s.id, i]));

  // ── 1. Who each stop may go to: the zones it's in (those with couriers) rule it ──
  const zones = (input.zones ?? []).filter((z) => z.couriers.length && z.path.length >= 3);
  const stopZones = stops.map((s) => zones.filter((z) => inside(s, z.path)));
  const stopAllowed: (number[] | null)[] = stopZones.map((zs) => {
    if (!zs.length) return null;
    const uids = new Set(zs.flatMap((z) => z.couriers));
    return couriers.map((c, i) => (uids.has(c.uid) ? i : -1)).filter((i) => i >= 0);
  });

  // ── Units: a pickup and the deliveries it feeds stay together ──
  const parent = stops.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  stops.forEach((s, i) => {
    const p = s.pickupId ? index.get(s.pickupId) : undefined;
    if (p != null && p !== i) parent[find(i)] = find(p);
  });
  const groups = new Map<number, number[]>();
  stops.forEach((_, i) => groups.set(find(i), [...(groups.get(find(i)) ?? []), i]));
  const units: Unit[] = [];
  for (const members of groups.values()) {
    // Pickups first, so the route keeps them ahead of their deliveries.
    members.sort((a, b) => Number(!!stops[a].pickupId) - Number(!!stops[b].pickupId) || a - b);
    const names = [...new Set(members.flatMap((i) => stopZones[i].map((z) => z.name)))];
    // A stop in zones none of whose couriers is picked: it, and whatever goes with it, is left out.
    const shut = members.find((i) => stopAllowed[i] !== null && stopAllowed[i]!.length === 0);
    if (shut != null) {
      const zs = stopZones[shut];
      for (const i of members) left.push({ id: stops[i].id, why: i === shut || members.length === 1 ? { kind: 'zone', zones: zs.map((z) => z.name), couriers: [...new Set(zs.flatMap((z) => z.couriers))] } : { kind: 'apart', other: stops[shut].id, zones: names } });
      continue;
    }
    let allowed: number[] | null = null;
    for (const i of members) {
      const a = stopAllowed[i];
      if (a) allowed = allowed ? allowed.filter((c) => a.includes(c)) : [...a];
    }
    if (allowed && !allowed.length) {
      for (const i of members) left.push({ id: stops[i].id, why: { kind: 'apart', other: stops[members.find((j) => j !== i) ?? i].id, zones: names } });
      continue;
    }
    const x = members.reduce((a, i) => a + pts[i].x, 0) / members.length;
    const y = members.reduce((a, i) => a + pts[i].y, 0) / members.length;
    units.push({ members, n: members.length, allowed, zones: names, x, y });
  }

  // ── Which couriers: each one picked, or as few as the stops need ──
  const total = units.reduce((a, u) => a + u.n, 0);
  const active = pickCouriers(units, couriers, !!input.fewest, total);
  const isActive = new Set(active);
  const can = (u: Unit) => (u.allowed ?? active).filter((c) => isActive.has(c));

  const max = couriers.map((c) => c.max);
  const fastest = input.balance === 'fastest';
  // Fastest grows areas as petals from the start; the even balances keep them as compact as the shares allow.
  const cost: Cost = fastest ? petals(origin) : dist;
  // ── How many each takes: even shares (within one) under each one's most, or as many as fit for Fastest ──
  const target = fastest ? [...max] : evenShares(active, max, total);
  // ── 2. Balanced clusters: seeds swept round the start, then centres moved to their stops ──
  let centres = seeds(units, active, origin, target);
  let owner = units.map(() => -1);
  for (let round = 0; round < 12; round++) {
    const next = assign(units, centres, target, max, can, cost);
    const same = next.every((o, i) => o === owner[i]);
    owner = next;
    if (same) break;
    centres = centresOf(units, owner, centres);
  }
  // ── 3. Stops on the edges move until the shares are even (Even time starts from even shares too) ──
  if (!fastest) evenOut(units, owner, centres, max, can, cost);
  // ── 4. Each route in order: nearest next, then 2-opt; a pickup before its delivery ──
  const routeOf = (c: number) => order(units.filter((_, u) => owner[u] === c).flatMap((u) => u.members), pts, origin, stops, index);
  const orders = new Map<number, number[]>(active.map((c) => [c, routeOf(c)]));
  const timeOf = (ord: number[]) => lengthOf(ord, pts, origin) / speed + ord.length * dwell;
  if (input.balance === 'time') evenTime(units, owner, orders, centres, max, can, pts, origin, stops, index, timeOf, cost);
  if (fastest) shorten(units, owner, orders, centres, max, can, pts, origin, stops, index, cost);
  // Neighbours swap edge stops wherever that shortens both routes together (the shares stay as they are): what undoes
  // two routes crossing.
  else swaps(units, owner, orders, can, pts, origin, stops, index, input.balance === 'time' ? timeOf : null);
  // ── 5. Routes that don't cross ──
  untangle(units, owner, orders, can, max, pts, origin, !!input.start, stops, index, input.balance, timeOf);

  // What didn't fit anyone's most stops: said against the courier it would have gone to.
  units.forEach((u, i) => {
    if (owner[i] >= 0) return;
    const best = closest(u, centres, can(u), cost);
    for (const s of u.members)
      left.push({
        id: stops[s].id,
        why: best >= 0 ? { kind: 'full', uid: couriers[best].uid, max: max[best], zones: u.zones } : { kind: 'zone', zones: u.zones, couriers: (u.allowed ?? []).map((c) => couriers[c].uid) },
      });
  });

  const routes: ShareRoute[] = active
    .slice()
    .sort((a, b) => a - b)
    .map((c) => {
      const ord = orders.get(c) ?? [];
      const m = lengthOf(ord, pts, origin);
      return { uid: couriers[c].uid, ids: ord.map((i) => stops[i].id), distanceM: Math.round(m), drivingS: Math.round(m / speed), totalS: Math.round(m / speed + ord.length * dwell) };
    })
    // As few as needed: a courier left with nothing isn't one of them.
    .filter((r) => !input.fewest || r.ids.length);
  left.sort((a, b) => (a.id < b.id ? -1 : 1));
  return { routes, left };
}

const emptyRoute = (uid: string): ShareRoute => ({ uid, ids: [], distanceM: 0, drivingS: 0, totalS: 0 });

/**
 * As few as needed: for stops only certain couriers may take (zones), enough of those couriers to hold them, the ones who
 * carry most first; then more, most first, until everyone's most stops hold every stop. Otherwise, every courier picked.
 */
function pickCouriers(units: Unit[], couriers: ShareCourier[], fewest: boolean, total: number): number[] {
  const all = couriers.map((_, i) => i);
  if (!fewest) return all;
  const byRoom = [...all].sort((a, b) => couriers[b].max - couriers[a].max || a - b);
  const chosen = new Set<number>();
  const room = () => [...chosen].reduce((a, c) => a + couriers[c].max, 0);
  // Each set of couriers a zone allows, and the stops that need one of them.
  const needs = new Map<string, { set: number[]; n: number }>();
  for (const u of units) {
    if (!u.allowed) continue;
    const key = u.allowed.join(',');
    needs.set(key, { set: u.allowed, n: (needs.get(key)?.n ?? 0) + u.n });
  }
  for (const { set, n } of [...needs.values()].sort((a, b) => b.n - a.n)) {
    let have = set.filter((c) => chosen.has(c)).reduce((a, c) => a + couriers[c].max, 0);
    for (const c of byRoom) {
      if (have >= n) break;
      if (!set.includes(c) || chosen.has(c)) continue;
      chosen.add(c);
      have += couriers[c].max;
    }
  }
  for (const c of byRoom) {
    if (room() >= total) break;
    chosen.add(c);
  }
  if (!chosen.size && byRoom.length) chosen.add(byRoom[0]);
  return all.filter((c) => chosen.has(c));
}

/**
 * Where each courier's area starts: a courier with stops only they (and their zone's others) may take starts in the middle
 * of those; the rest share the other stops swept round the start, from the widest gap, in turns as big as their shares.
 */
function seeds(units: Unit[], active: number[], origin: P, target: number[]): P[] {
  const centres: P[] = [];
  const seeded = new Set<number>();
  for (const c of active) {
    const own = units.filter((u) => u.allowed?.includes(c));
    if (!own.length) continue;
    centres[c] = { x: own.reduce((a, u) => a + u.x * u.n, 0) / own.reduce((a, u) => a + u.n, 0), y: own.reduce((a, u) => a + u.y * u.n, 0) / own.reduce((a, u) => a + u.n, 0) };
    seeded.add(c);
  }
  const free = active.filter((c) => !seeded.has(c));
  const open = sweep(units.filter((u) => !u.allowed), origin);
  const want = free.reduce((a, c) => a + Math.max(1, target[c]), 0);
  let at = 0;
  free.forEach((c, k) => {
    const size = k === free.length - 1 ? open.length - at : Math.round((open.length * Math.max(1, target[c])) / Math.max(1, want));
    const part = open.slice(at, at + Math.max(0, size));
    at += part.length;
    centres[c] = part.length ? { x: part.reduce((a, u) => a + u.x, 0) / part.length, y: part.reduce((a, u) => a + u.y, 0) / part.length } : { ...origin };
  });
  return centres;
}

/** Units round [origin] by compass direction, starting just after the widest empty gap (runs.dart `_bySweep`). */
function sweep(units: Unit[], origin: P): Unit[] {
  if (units.length < 2) return [...units];
  const angle = (u: Unit) => Math.atan2(u.y - origin.y, u.x - origin.x);
  const sorted = [...units].sort((a, b) => angle(a) - angle(b) || a.x - b.x || a.y - b.y);
  let widest = -1;
  let startAt = 0;
  for (let i = 0; i < sorted.length; i++) {
    let gap = angle(sorted[i]) - angle(sorted[(i - 1 + sorted.length) % sorted.length]);
    if (i === 0) gap += 2 * Math.PI;
    if (gap > widest) {
      widest = gap;
      startAt = i;
    }
  }
  return [...sorted.slice(startAt), ...sorted.slice(0, startAt)];
}

const closest = (u: P, centres: P[], among: number[], cost: Cost) => among.reduce((b, c) => (b < 0 || cost(u, centres[c]) < cost(u, centres[b]) ? c : b), -1);

/**
 * Each unit to the nearest centre with room, the units with most to lose placed first (regret: how much further their
 * next-best courier is). Room is the courier's share; whatever doesn't fit a share goes, if it fits, past it up to their
 * most (step 3 evens it out); otherwise it's left out (-1).
 */
function assign(units: Unit[], centres: P[], target: number[], max: number[], can: (u: Unit) => number[], cost: Cost): number[] {
  const owner = units.map(() => -1);
  const ranked = units.map((u, i) => {
    const cs = can(u)
      .map((c) => ({ c, d: cost(u, centres[c]) }))
      .sort((a, b) => a.d - b.d || a.c - b.c);
    return { i, cs, regret: cs.length > 1 ? cs[1].d - cs[0].d : Infinity };
  });
  ranked.sort((a, b) => b.regret - a.regret || a.i - b.i);
  const load = max.map(() => 0);
  const over: typeof ranked = [];
  for (const r of ranked) {
    const u = units[r.i];
    const c = r.cs.find((x) => load[x.c] + u.n <= target[x.c]);
    if (c) {
      owner[r.i] = c.c;
      load[c.c] += u.n;
    } else over.push(r);
  }
  for (const r of over) {
    const u = units[r.i];
    const c = r.cs.find((x) => load[x.c] + u.n <= max[x.c]);
    if (c) {
      owner[r.i] = c.c;
      load[c.c] += u.n;
    }
  }
  return owner;
}

function centresOf(units: Unit[], owner: number[], was: P[]): P[] {
  const sum = was.map(() => ({ x: 0, y: 0, n: 0 }));
  units.forEach((u, i) => {
    const c = owner[i];
    if (c < 0) return;
    sum[c].x += u.x * u.n;
    sum[c].y += u.y * u.n;
    sum[c].n += u.n;
  });
  return was.map((p, c) => (p && sum[c]?.n ? { x: sum[c].x / sum[c].n, y: sum[c].y / sum[c].n } : p));
}

/** The route's length in metres: from the start (when there is one) through each stop in [ord]. */
function lengthOf(ord: number[], pts: P[], origin: P | null): number {
  let m = 0;
  ord.forEach((s, k) => (m += k === 0 ? (origin ? dist(origin, pts[s]) : 0) : dist(pts[ord[k - 1]], pts[s])));
  return m;
}

/** Nearest stop next from the start, then 2-opt until no turn shortens it; a pickup goes just before its delivery. */
function order(members: number[], pts: P[], origin: P, stops: ShareStop[], index: Map<string, number>): number[] {
  if (members.length < 2) return [...members];
  const left = [...members].sort((a, b) => a - b);
  const out: number[] = [];
  let at = origin;
  while (left.length) {
    let bi = 0;
    let bd = Infinity;
    left.forEach((s, k) => {
      const d = dist(at, pts[s]);
      if (d < bd - 1e-9) {
        bd = d;
        bi = k;
      }
    });
    const [s] = left.splice(bi, 1);
    out.push(s);
    at = pts[s];
  }
  return pickupsFirst(twoOpt(out, pts, origin), stops, index);
}

/** 2-opt on an open route from [origin]: a stretch turned round wherever that's shorter, until no turn helps. */
function twoOpt(route: number[], pts: P[], origin: P, maxPasses = 40): number[] {
  const r = [...route];
  const n = r.length;
  if (n < 3) return r;
  const at = (k: number) => (k < 0 ? origin : pts[r[k]]);
  for (let pass = 0; pass < maxPasses; pass++) {
    let improved = false;
    for (let i = 0; i < n - 1; i++) {
      for (let k = i + 1; k < n; k++) {
        const a = at(i - 1);
        const b = at(i);
        const c = at(k);
        const now = dist(a, b) + (k + 1 < n ? dist(c, at(k + 1)) : 0);
        const turned = dist(a, c) + (k + 1 < n ? dist(b, at(k + 1)) : 0);
        if (turned < now - 0.5) {
          for (let lo = i, hi = k; lo < hi; lo++, hi--) [r[lo], r[hi]] = [r[hi], r[lo]];
          improved = true;
        }
      }
    }
    if (!improved) break;
  }
  return r;
}

/** A pickup just before the first delivery it feeds, as PickupOrder keeps it; everything else keeps its place. */
function pickupsFirst(route: number[], stops: ShareStop[], index: Map<string, number>): number[] {
  const out = [...route];
  for (let i = 0; i < out.length; i++) {
    const p = stops[out[i]].pickupId;
    const pi = p != null ? index.get(p) : undefined;
    const j = pi != null ? out.indexOf(pi) : -1;
    if (j > i) {
      out.splice(j, 1);
      out.splice(i, 0, pi!);
      i++;
    }
  }
  return out;
}

/** What taking [members] off [ord] saves in metres, and the route without them. */
function without(ord: number[], members: number[], pts: P[], origin: P): { saved: number; rest: number[] } {
  const rest = ord.filter((s) => !members.includes(s));
  return { saved: lengthOf(ord, pts, origin) - lengthOf(rest, pts, origin), rest };
}

/** [s] taken off [ord]: what that saves in metres, from its two neighbours, and the route without it. */
function taken(ord: number[], s: number, pts: P[], origin: P): { saved: number; rest: number[] } {
  const k = ord.indexOf(s);
  const prev = k > 0 ? pts[ord[k - 1]] : origin;
  const next = k + 1 < ord.length ? pts[ord[k + 1]] : null;
  return { saved: dist(prev, pts[s]) + (next ? dist(pts[s], next) - dist(prev, next) : 0), rest: [...ord.slice(0, k), ...ord.slice(k + 1)] };
}

/** [members] each put into [ord] where they add least; what that adds in metres. */
function withAdded(ord: number[], members: number[], pts: P[], origin: P): { added: number; route: number[] } {
  const route = [...ord];
  let added = 0;
  for (const s of members) {
    let best = route.length;
    let cost = Infinity;
    for (let k = 0; k <= route.length; k++) {
      const a = k === 0 ? origin : pts[route[k - 1]];
      const b = k < route.length ? pts[route[k]] : null;
      const c = dist(a, pts[s]) + (b ? dist(pts[s], b) - dist(a, b) : 0);
      if (c < cost - 1e-9) {
        cost = c;
        best = k;
      }
    }
    route.splice(best, 0, s);
    added += cost;
  }
  return { added, route };
}

/** The [k] couriers [u] may go to whose areas are nearest it (moves are only tried between neighbours). */
const nearest = (u: P, centres: P[], among: number[], k: number, cost: Cost) => [...among].sort((a, b) => cost(u, centres[a]) - cost(u, centres[b]) || a - b).slice(0, k);

/** Even shares of [total], within one of each other, none over its courier's most; the rest of [target] is 0. */
function evenShares(active: number[], max: number[], total: number): number[] {
  const t = max.map(() => 0);
  const byMax = [...active].sort((a, b) => max[a] - max[b] || a - b);
  let rem = total;
  byMax.forEach((c, k) => {
    t[c] = Math.min(max[c], Math.floor(rem / (byMax.length - k)));
    rem -= t[c];
  });
  // What's left after flooring, one each, to those with room: the shares stay within one.
  for (let pass = 0; rem > 0 && pass <= total; pass++) {
    let gave = false;
    for (const c of [...active].sort((a, b) => t[a] - t[b] || a - b)) {
      if (rem <= 0) break;
      if (t[c] < max[c]) {
        t[c]++;
        rem--;
        gave = true;
      }
    }
    if (!gave) break;
  }
  return t;
}

/**
 * Even stops: while one courier has two or more stops more than another they could give to, the stop on the edge of their
 * area that's cheapest to move (nearest the other's middle, against its own) moves. Each move narrows the gap, so it ends.
 */
function evenOut(units: Unit[], owner: number[], centres: P[], max: number[], can: (u: Unit) => number[], cost: Cost) {
  const size = max.map(() => 0);
  units.forEach((u, i) => owner[i] >= 0 && (size[owner[i]] += u.n));
  for (let step = 0; step < 5000; step++) {
    let best: { i: number; to: number; cost: number } | null = null;
    units.forEach((u, i) => {
      const from = owner[i];
      if (from < 0) return;
      for (const to of can(u)) {
        if (to === from || size[from] - size[to] <= u.n || size[to] + u.n > max[to]) continue;
        const c = cost(u, centres[to]) - cost(u, centres[from]);
        if (!best || c < best.cost - 1e-9) best = { i, to, cost: c };
      }
    });
    if (!best) return;
    const { i, to } = best as { i: number; to: number };
    size[owner[i]] -= units[i].n;
    size[to] += units[i].n;
    owner[i] = to;
  }
}

/**
 * Even time: the longest day gives the stop on its edge whose move to a neighbouring area most shortens the longer of the
 * two, until no move helps. Each route is put in order again after a move.
 */
function evenTime(
  units: Unit[],
  owner: number[],
  orders: Map<number, number[]>,
  centres: P[],
  max: number[],
  can: (u: Unit) => number[],
  pts: P[],
  origin: P,
  stops: ShareStop[],
  index: Map<string, number>,
  timeOf: (ord: number[]) => number,
  cost: Cost,
) {
  const size = max.map(() => 0);
  units.forEach((u, i) => owner[i] >= 0 && (size[owner[i]] += u.n));
  const time = new Map([...orders].map(([c, o]) => [c, timeOf(o)]));
  for (let step = 0; step < 400; step++) {
    const longest = [...time].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
    if (!longest) return;
    const [from, tFrom] = longest;
    let best: { i: number; to: number; worst: number; fromOrd: number[]; toOrd: number[] } | null = null;
    units.forEach((u, i) => {
      if (owner[i] !== from) return;
      const off = without(orders.get(from)!, u.members, pts, origin);
      const tA = timeOf(off.rest);
      for (const to of nearest(u, centres, can(u).filter((c) => c !== from && time.has(c)), 3, cost)) {
        if (size[to] + u.n > max[to]) continue;
        const on = withAdded(orders.get(to)!, u.members, pts, origin);
        const worst = Math.max(tA, timeOf(on.route));
        if (worst < tFrom - 1 && (!best || worst < best.worst - 1e-6)) best = { i, to, worst, fromOrd: off.rest, toOrd: on.route };
      }
    });
    if (!best) return;
    const b = best as { i: number; to: number; fromOrd: number[]; toOrd: number[] };
    owner[b.i] = b.to;
    size[from] -= units[b.i].n;
    size[b.to] += units[b.i].n;
    for (const [c, o] of [[from, b.fromOrd], [b.to, b.toOrd]] as [number, number[]][]) {
      const again = pickupsFirst(twoOpt(o, pts, origin), stops, index);
      orders.set(c, again);
      time.set(c, timeOf(again));
    }
  }
}

/**
 * Two neighbouring routes swap a stop each wherever that makes them shorter together, the shares staying as they are (and,
 * for Even time, the longer of the two no longer): what undoes two routes crossing. Each stop tries its nearest stops on
 * other routes; a few sweeps, each route put in order again after one.
 */
function swaps(
  units: Unit[],
  owner: number[],
  orders: Map<number, number[]>,
  can: (u: Unit) => number[],
  pts: P[],
  origin: P,
  stops: ShareStop[],
  index: Map<string, number>,
  timeOf: ((ord: number[]) => number) | null,
) {
  // Each stop's nearest stops, found by sweeping out along x from it until x alone is further than the furthest kept.
  const K = 6;
  const byX = units.map((_, i) => i).sort((a, b) => units[a].x - units[b].x || a - b);
  const rank = new Map(byX.map((u, r) => [u, r]));
  const near = units.map((u, i) => {
    const best: { j: number; d: number }[] = [];
    const keep = (j: number) => {
      const d = dist(u, units[j]);
      if (best.length === K && !(d < best[K - 1].d)) return;
      let k = best.length;
      while (k > 0 && (best[k - 1].d > d || (best[k - 1].d === d && best[k - 1].j > j))) k--;
      best.splice(k, 0, { j, d });
      if (best.length > K) best.pop();
    };
    const r = rank.get(i)!;
    for (let lo = r - 1, hi = r + 1; lo >= 0 || hi < byX.length; ) {
      const far = best.length === K ? best[K - 1].d : Infinity;
      const dl = lo >= 0 ? u.x - units[byX[lo]].x : Infinity;
      const dh = hi < byX.length ? units[byX[hi]].x - u.x : Infinity;
      if (Math.min(dl, dh) > far) break;
      if (dl <= dh) keep(byX[lo--]);
      else keep(byX[hi++]);
    }
    return best.map((x) => x.j);
  });
  const len = new Map([...orders].map(([c, o]) => [c, lengthOf(o, pts, origin)]));
  // A route with one stop swapped for another, and its new length: priced from the stop's neighbours and the gap the
  // other fits best (a pickup and its delivery, priced whole).
  const swapped = (ord: number[], c: number, out: Unit, inn: Unit): { route: number[]; m: number } => {
    if (out.n === 1 && inn.n === 1) {
      const k = ord.indexOf(out.members[0]);
      const at = (q: number) => (q < 0 ? origin : pts[ord[q]]);
      const s0 = pts[ord[k]];
      const next = k + 1 < ord.length ? pts[ord[k + 1]] : null;
      const saved = dist(at(k - 1), s0) + (next ? dist(s0, next) - dist(at(k - 1), next) : 0);
      const rest = [...ord.slice(0, k), ...ord.slice(k + 1)];
      const put = withAdded(rest, inn.members, pts, origin);
      return { route: put.route, m: len.get(c)! - saved + put.added };
    }
    const route = withAdded(without(ord, out.members, pts, origin).rest, inn.members, pts, origin).route;
    return { route, m: lengthOf(route, pts, origin) };
  };
  for (let sweepNo = 0; sweepNo < 3; sweepNo++) {
    let moved = false;
    for (let i = 0; i < units.length; i++) {
      const a = owner[i];
      if (a < 0) continue;
      for (const j of near[i]) {
        const b = owner[j];
        if (b < 0 || b === a || units[i].n !== units[j].n) continue;
        if (!can(units[j]).includes(a) || !can(units[i]).includes(b)) continue;
        const na = swapped(orders.get(a)!, a, units[i], units[j]);
        const nb = swapped(orders.get(b)!, b, units[j], units[i]);
        if (na.m + nb.m >= len.get(a)! + len.get(b)! - 5) continue;
        // Even time: the longer of the two no longer (each keeps as many stops, so the driving decides).
        if (timeOf && Math.max(na.m, nb.m) > Math.max(len.get(a)!, len.get(b)!) + 5) continue;
        owner[i] = b;
        owner[j] = a;
        orders.set(a, na.route);
        orders.set(b, nb.route);
        len.set(a, na.m);
        len.set(b, nb.m);
        moved = true;
        break;
      }
    }
    for (const [c, o] of orders) {
      const again = pickupsFirst(twoOpt(o, pts, origin), stops, index);
      orders.set(c, again);
      len.set(c, lengthOf(again, pts, origin));
    }
    if (!moved) break;
  }
}

/** Fastest: a stop on an edge moves to a neighbour wherever that shortens the two routes together, until none does. */
function shorten(
  units: Unit[],
  owner: number[],
  orders: Map<number, number[]>,
  centres: P[],
  max: number[],
  can: (u: Unit) => number[],
  pts: P[],
  origin: P,
  stops: ShareStop[],
  index: Map<string, number>,
  cost: Cost,
) {
  const size = max.map(() => 0);
  units.forEach((u, i) => owner[i] >= 0 && (size[owner[i]] += u.n));
  for (let sweepNo = 0; sweepNo < 3; sweepNo++) {
    let moved = false;
    units.forEach((u, i) => {
      const from = owner[i];
      if (from < 0) return;
      const off = u.n === 1 ? taken(orders.get(from)!, u.members[0], pts, origin) : without(orders.get(from)!, u.members, pts, origin);
      for (const to of nearest(u, centres, can(u).filter((c) => c !== from && orders.has(c)), 3, cost)) {
        if (size[to] + u.n > max[to]) continue;
        const on = withAdded(orders.get(to)!, u.members, pts, origin);
        if (on.added < off.saved - 1) {
          owner[i] = to;
          size[from] -= u.n;
          size[to] += u.n;
          orders.set(from, off.rest);
          orders.set(to, on.route);
          moved = true;
          return;
        }
      }
    });
    for (const [c, o] of orders) orders.set(c, pickupsFirst(twoOpt(o, pts, origin), stops, index));
    if (!moved) break;
  }
}

/**
 * How many moves [untangle] tries at most: a count, not the clock, so the same input gives the same answer; fewer the more
 * stops there are (each try costs more), so 500 stops still take about 200 ms.
 */
const untangleTries = (stops: number) => Math.min(2000, Math.round(50_000 / Math.max(1, stops)));

/**
 * Routes that don't cross: each pair of legs of two couriers' routes that cross (the legs out of the start counted, as
 * Share out counts them) tries these in turn, keeping the first that lowers the crossings over every route: one of the two
 * routes turned round from the crossing on (from the start: its first leg leaves it towards the route's other end); their
 * tails after the crossing swapped;
 * one to three stops either side of it swapped, so both keep their counts; one stop passed across. A changed route is put
 * back in order (2-opt, a pickup first) unless that brings a crossing back; a turned route keeps its direction. Zones,
 * most stops and a pickup with its deliveries are kept, and so is the balance: Even stops' shares get no further apart;
 * Even time's days get no further apart and the longest grows by a tenth at most; the km by a tenth (Fastest: a
 * twentieth).
 */
function untangle(
  units: Unit[],
  owner: number[],
  orders: Map<number, number[]>,
  can: (u: Unit) => number[],
  max: number[],
  pts: P[],
  origin: P,
  fromStart: boolean,
  stops: ShareStop[],
  index: Map<string, number>,
  balance: Balance,
  timeOf: (ord: number[]) => number,
) {
  const cs = [...orders.keys()].sort((a, b) => a - b);
  if (cs.length < 2) return;
  const unitOf: number[] = [];
  units.forEach((u, k) => u.members.forEach((s) => (unitOf[s] = k)));
  // A leg is a pair of stops, the start being stop N; its key is the same either way round.
  type Leg = [number, number];
  const N = pts.length;
  const at = (s: number) => (s === N ? origin : pts[s]);
  const legsOf = (r: number[]): Leg[] => {
    const ns = fromStart ? [N, ...r] : r;
    return ns.slice(1).map((s, k) => [ns[k], s]);
  };
  const keyOf = ([u, v]: Leg) => (u < v ? u * (N + 1) + v : v * (N + 1) + u);
  const meet = (l: Leg, m: Leg) => {
    const p = at(l[0]);
    const q = at(l[1]);
    const r = at(m[0]);
    const s = at(m[1]);
    if (Math.max(p.x, q.x) < Math.min(r.x, s.x) || Math.max(r.x, s.x) < Math.min(p.x, q.x) || Math.max(p.y, q.y) < Math.min(r.y, s.y) || Math.max(r.y, s.y) < Math.min(p.y, q.y)) return false;
    return cross(p, q, r, s);
  };
  const count = (xs: Leg[], ys: Leg[]) => {
    let n = 0;
    for (const l of xs) for (const m of ys) if (meet(l, m)) n++;
    return n;
  };
  const legs = new Map(cs.map((c) => [c, legsOf(orders.get(c)!)]));
  const len = new Map(cs.map((c) => [c, lengthOf(orders.get(c)!, pts, origin)]));
  const day = new Map(cs.map((c) => [c, timeOf(orders.get(c)!)]));
  const ver = new Map(cs.map((c) => [c, 0]));
  let km = [...len.values()].reduce((a, b) => a + b, 0);
  const kmCap = km * (balance === 'fastest' ? 1.05 : 1.1) + 1;
  const dayCap = Math.max(...day.values()) * 1.1 + 1;
  const gapCap = Math.max(...day.values()) - Math.min(...day.values()) + 1;
  const turned = new Set<number>();
  let tries = 0;

  /** What [a]'s and [b]'s routes becoming [na] and [nb] does to the crossings over every route: only changed legs counted. */
  const change = (a: number, b: number, na: number[], nb: number[]) => {
    const was = [...legs.get(a)!, ...legs.get(b)!];
    const la = legsOf(na);
    const lb = legsOf(nb);
    const wasKeys = new Set(was.map(keyOf));
    const nowKeys = new Set([...la, ...lb].map(keyOf));
    const gone = was.filter((l) => !nowKeys.has(keyOf(l)));
    const added = [...la, ...lb].filter((l) => !wasKeys.has(keyOf(l)));
    let d = count(la, lb) - count(legs.get(a)!, legs.get(b)!);
    for (const c of cs) if (c !== a && c !== b) d += count(added, legs.get(c)!) - count(gone, legs.get(c)!);
    return d;
  };

  /** [a]'s and [b]'s routes as [na] and [nb], when every rule holds and that lowers the crossings; [turn]: the one turned. */
  const attempt = (a: number, b: number, na: number[], nb: number[], turn: number | null): boolean => {
    tries++;
    const ra = orders.get(a)!;
    const rb = orders.get(b)!;
    if (na.length > max[a] || nb.length > max[b]) return false;
    // Even stops: the two shares get no further apart, so neither leaves the range the others' shares are in.
    if (balance === 'stops' && Math.abs(na.length - nb.length) > Math.abs(ra.length - rb.length)) return false;
    // Whatever changes courier is allowed them by its zones, and everything that goes with it changes too.
    for (const [r, c] of [[na, a], [nb, b]] as [number[], number][]) {
      const on = new Set(r);
      for (const s of r) {
        const u = units[unitOf[s]];
        if (owner[unitOf[s]] !== c && (!can(u).includes(c) || u.members.some((m) => !on.has(m)))) return false;
      }
    }
    // A route the move leaves as it was isn't put in order again.
    const fa = na === ra ? ra : pickupsFirst(na, stops, index);
    const fb = nb === rb ? rb : pickupsFirst(nb, stops, index);
    const tidy = (r: number[], c: number) => (r === orders.get(c) || turned.has(c) || turn === c ? r : pickupsFirst(twoOpt(r, pts, origin), stops, index));
    const ta = tidy(fa, a);
    const tb = tidy(fb, b);
    const rest = cs.filter((c) => c !== a && c !== b).map((c) => day.get(c)!);
    // In order again, unless that brings a crossing back.
    for (const [xa, xb] of ta === fa && tb === fb ? [[fa, fb]] : [[ta, tb], [fa, fb]]) {
      const ma = lengthOf(xa, pts, origin);
      const mb = lengthOf(xb, pts, origin);
      if (km - len.get(a)! - len.get(b)! + ma + mb > kmCap) continue;
      if (balance === 'time') {
        const days = [...rest, timeOf(xa), timeOf(xb)];
        if (Math.max(...days) > dayCap || Math.max(...days) - Math.min(...days) > gapCap) continue;
      }
      if (change(a, b, xa, xb) >= 0) continue;
      km += ma + mb - len.get(a)! - len.get(b)!;
      for (const [c, r] of [[a, xa], [b, xb]] as [number, number[]][]) {
        orders.set(c, r);
        legs.set(c, legsOf(r));
        len.set(c, lengthOf(r, pts, origin));
        day.set(c, timeOf(r));
        ver.set(c, ver.get(c)! + 1);
        for (const s of r) owner[unitOf[s]] = c;
        if (turn === c) turned.add(c);
      }
      return true;
    }
    return false;
  };

  /** The moves for [a]'s leg [i] crossing [b]'s leg [j], in turn, until one is kept. */
  const uncross = (a: number, i: number, b: number, j: number): boolean => {
    const ra = orders.get(a)!;
    const rb = orders.get(b)!;
    // Where each crossing leg leads: the stops before it are ahead of the crossing, the rest after it.
    const ea = fromStart ? i : i + 1;
    const eb = fromStart ? j : j + 1;
    // 1. Turned round from the crossing on: the crossing leg goes to the route's last stop instead.
    if (ra.length - ea > 1 && attempt(a, b, [...ra.slice(0, ea), ...ra.slice(ea).reverse()], rb, a)) return true;
    if (rb.length - eb > 1 && attempt(a, b, ra, [...rb.slice(0, eb), ...rb.slice(eb).reverse()], b)) return true;
    // 2. The tails after the crossing swapped.
    if ((ea > 0 || eb > 0) && attempt(a, b, [...ra.slice(0, ea), ...rb.slice(eb)], [...rb.slice(0, eb), ...ra.slice(ea)], null)) return true;
    // 3. One to three stops either side of the crossing swapped (a pickup and its deliveries don't swap).
    for (let k = 1; k <= 3; k++)
      for (const sa of [ea - k, ea])
        for (const sb of [eb - k, eb]) {
          if (sa < 0 || sa + k > ra.length || sb < 0 || sb + k > rb.length) continue;
          const xa = ra.slice(sa, sa + k);
          const xb = rb.slice(sb, sb + k);
          if ([...xa, ...xb].some((s) => units[unitOf[s]].n > 1)) continue;
          if (attempt(a, b, [...ra.slice(0, sa), ...xb, ...ra.slice(sa + k)], [...rb.slice(0, sb), ...xa, ...rb.slice(sb + k)], null)) return true;
        }
    // 4. One stop passed across, with whatever goes with it, where it adds least.
    for (const [from, to, r, e] of [[a, b, ra, ea], [b, a, rb, eb]] as [number, number, number[], number][])
      for (const q of [e - 1, e]) {
        if (q < 0 || q >= r.length) continue;
        const u = units[unitOf[r[q]]];
        const rest = r.filter((s) => !u.members.includes(s));
        const into = withAdded(orders.get(to)!, u.members, pts, origin).route;
        if (attempt(a, b, from === a ? rest : into, from === a ? into : rest, null)) return true;
      }
    // 5. Last, a stretch of two to six stops of one route turned round, from the crossing on or up to it.
    for (const [r, e, mine] of [[ra, ea, true], [rb, eb, false]] as [number[], number, boolean][])
      for (let k = 2; k <= 6; k++)
        for (const lo of [e, e - k]) {
          if (lo < 0 || lo + k > r.length || (lo === e && lo + k === r.length)) continue;
          const t = [...r.slice(0, lo), ...r.slice(lo, lo + k).reverse(), ...r.slice(lo + k)];
          if (attempt(a, b, mine ? t : ra, mine ? rb : t, null)) return true;
        }
    return false;
  };

  // Each pair of routes' crossing legs, worked out again only once one of the two has changed.
  const hits = new Map<string, Leg[]>();
  const hitsOf = (a: number, b: number) => {
    const key = `${a}.${ver.get(a)} ${b}.${ver.get(b)}`;
    let h = hits.get(key);
    if (!h) {
      h = [];
      const A = legs.get(a)!;
      const B = legs.get(b)!;
      for (let i = 0; i < A.length; i++) for (let j = 0; j < B.length; j++) if (meet(A[i], B[j])) h.push([i, j]);
      hits.set(key, h);
    }
    return h;
  };
  // Crossings in a fixed order, from the first again after each move kept; one that's had its tries isn't tried again
  // until one of its routes changes.
  const done = new Set<string>();
  const most = untangleTries(N);
  scan: while (tries < most) {
    for (let x = 0; x < cs.length; x++)
      for (let y = x + 1; y < cs.length; y++) {
        const a = cs[x];
        const b = cs[y];
        for (const [i, j] of hitsOf(a, b)) {
          const key = `${a}.${ver.get(a)}.${i} ${b}.${ver.get(b)}.${j}`;
          if (done.has(key)) continue;
          done.add(key);
          if (uncross(a, i, b, j)) continue scan;
          if (tries >= most) return;
        }
      }
    return;
  }
}

/**
 * How many times two different couriers' routes cross: each pair of their legs (from the start, then stop to stop) that
 * meet away from a shared end. What makes a split trustworthy (Routific's benchmark counts it).
 */
export function crossings(routes: { ids: string[] }[], at: (id: string) => Spot | undefined, start: Spot | null): number {
  const all = routes.flatMap((r) => r.ids.map(at).filter((p): p is Spot => !!p));
  if (!all.length) return 0;
  const proj = projector(start ? [...all, start] : all);
  const legs = routes.map((r) => {
    const ps = r.ids.map(at).filter((p): p is Spot => !!p).map(proj);
    const path = start ? [proj(start), ...ps] : ps;
    return path.slice(1).map((b, k) => [path[k], b] as [P, P]);
  });
  let n = 0;
  for (let a = 0; a < legs.length; a++)
    for (let b = a + 1; b < legs.length; b++)
      for (const s of legs[a])
        for (const t of legs[b]) if (cross(s[0], s[1], t[0], t[1])) n++;
  return n;
}

/** Whether segments pq and rs cross at a point that isn't an end of both (a shared start doesn't count). */
function cross(p: P, q: P, r: P, s: P): boolean {
  const same = (a: P, b: P) => Math.abs(a.x - b.x) < 0.01 && Math.abs(a.y - b.y) < 0.01;
  if (same(p, r) || same(p, s) || same(q, r) || same(q, s)) return false;
  const o = (a: P, b: P, c: P) => Math.sign((b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x));
  return o(p, q, r) !== o(p, q, s) && o(r, s, p) !== o(r, s, q) && o(p, q, r) !== 0 && o(r, s, p) !== 0;
}
